import { FC, useMemo } from "react";
import { Box } from "@mui/material";
import { generateHighChartsDataFromCommitments } from "../utils/helpers/generateHighChartsDataFromCommitments";
import { getBillingExplorerWidgetTooltipValueFormatter } from "../../../adding-widget/widget-create/widget-setups/billing-explorer/components/high-chart-view/utils/helpers/getBillingExplorerWidgetTooltipValueFormatter";
import { generateHighChartsTotalCostsFromCommitments } from "../utils/helpers/generateHighChartsTotalCostsFromCommitments";
import {
  CommitmentsGroupResponse,
  WidgetCommitmentsUtilizationVisualization,
} from "@/services/cloudchipr.api";
import { aggregateHighChartDataSeriesMap } from "@/storybook/c8r-highcharts/common/utils/helpers/aggregateHighChartDataSeriesMap";
import { HighchartsProvider } from "@/storybook/c8r-highcharts/common/components/HighchartsProvider";
import { MultiTypeHighcharts } from "@/storybook/c8r-highcharts/MultiTypeHighcharts";
import { stackedChartHeadingLabelFormatter } from "@/storybook/c8r-highcharts/common/utils/helpers/stackedChartHeadingLabelFormatter";
import { getHighChartsColumnDataLabels } from "@/storybook/c8r-highcharts/common/utils/helpers/getHighChartsColumnDataLabels";
import { getHighChartsYAxisLabelsFormatter } from "@/storybook/c8r-highcharts/common/utils/helpers/getHighChartsYAxisLabelsFormatter";
import { chartTypeFromRechartToHighCharts } from "@/components/utils/helpers/chartTypeFromRechartToHighCharts";
import { HighchartsChartType } from "@/storybook/c8r-highcharts/common/components/HighchartsChartTypeSelect";

interface CommitmentUtilizationWidgetHighChartProps {
  loading?: boolean;
  hasAggregations?: boolean;
  data?: CommitmentsGroupResponse[];
  visualization: WidgetCommitmentsUtilizationVisualization;
}

export const CommitmentUtilizationWidgetHighChart: FC<
  CommitmentUtilizationWidgetHighChartProps
> = ({ data, visualization, loading, hasAggregations }) => {
  const { chart_base_type, chart_type, show_data_labels, show_legends, scale } =
    visualization;
  const chartType = chartTypeFromRechartToHighCharts(
    chart_type ?? "stacked_column",
  ) as HighchartsChartType;

  const { costBased, percentBased } = useMemo(
    () => generateHighChartsDataFromCommitments(data),
    [data],
  );

  const chartDataCostBasedMap = useMemo(
    () => aggregateHighChartDataSeriesMap(costBased),
    [costBased],
  );

  const chartDataPercentBasedMap = useMemo(
    () => aggregateHighChartDataSeriesMap(percentBased),
    [percentBased],
  );

  const totalCostsMap = useMemo(
    () => generateHighChartsTotalCostsFromCommitments(data),
    [data],
  );

  const columnDataLabels = useMemo(
    () => getHighChartsColumnDataLabels(chartType, chart_base_type),
    [chart_base_type, chartType],
  );

  const yAxisLabelsFormatter = useMemo(
    () => getHighChartsYAxisLabelsFormatter(chart_base_type ?? "percent"),
    [chart_base_type],
  );

  const chartData = chart_base_type === "percent" ? percentBased : costBased;

  const tooltipValueFormatter = useMemo(
    () =>
      getBillingExplorerWidgetTooltipValueFormatter(
        chartDataCostBasedMap,
        chartDataPercentBasedMap,
        totalCostsMap,
      ),
    [chartDataCostBasedMap, chartDataPercentBasedMap, totalCostsMap],
  );

  return (
    <Box height={hasAggregations ? 370 : 445}>
      <HighchartsProvider>
        <MultiTypeHighcharts
          loading={loading}
          chartScale={scale}
          chartData={chartData}
          chartType={chartType}
          showLegends={show_legends}
          showDataLabels={show_data_labels}
          yAxisLabelsFormatter={yAxisLabelsFormatter}
          percentBased={chart_base_type === "percent"}
          tooltipMarkupGenerator={tooltipValueFormatter}
          noDataMessage="No data by your selected date range."
          columnDataLabels={columnDataLabels}
          stackedChartHeadingLabelFormatter={
            chart_base_type === "cost"
              ? stackedChartHeadingLabelFormatter
              : undefined
          }
        />
      </HighchartsProvider>
    </Box>
  );
};
